<template>
  <section class="view-stack">
    <div class="section-actions">
      <el-button icon="el-icon-refresh" round :loading="loading" @click="loadBalances">
        刷新
      </el-button>
    </div>

    <section class="plan-grid">
      <article
        v-for="group in groups"
        :key="group.balanceId || group.groupType"
        class="plan-card subscription-card"
        :class="[
          { featured: group.groupType === 'BALANCE' },
          `subscription-card--${getQuotaState(group).tone}`
        ]"
      >
        <div class="subscription-card-heading">
          <span>{{ getGroupLabel(group.groupType) }}</span>
          <span class="subscription-status" :class="`is-${getQuotaState(group).tone}`">
            {{ getQuotaState(group).label }}
          </span>
        </div>
        <div class="subscription-remaining">
          <span>当前可用额度</span>
          <strong>{{ formatAmount(getCurrentAvailableAmount(group)) }}</strong>
        </div>
        <p>{{ getGroupDescription(group) }}</p>
        <div class="subscription-metrics">
          <div>
            <label>{{ group.groupType === 'MONTH_CARD' ? '每日额度' : '余额' }}</label>
            <b>{{ formatAmount(group.balanceAmount) }}</b>
          </div>
          <div>
            <label>已使用</label>
            <b>{{ formatAmount(group.usedAmount) }}</b>
          </div>
          <div v-if="group.groupType === 'MONTH_CARD'">
            <label>有效期总额度</label>
            <b>{{ formatAmount(group.totalAmount) }}</b>
          </div>
          <div v-if="group.groupType === 'MONTH_CARD'">
            <label>有效期已使用</label>
            <b>{{ formatAmount(group.totalUsedAmount) }}</b>
          </div>
          <div v-if="group.groupType === 'MONTH_CARD'">
            <label>有效期剩余</label>
            <b>{{ formatAmount(getTotalRemainingAmount(group)) }}</b>
          </div>
          <div>
            <label>到期时间</label>
            <b>{{ formatDateTime(group.expireTime) }}</b>
          </div>
        </div>
        <div v-if="false" class="day-usage-reset">
          <p>可清零当前日额度，并继续使用今日额度</p>
          <el-button
            type="warning"
            plain
            :loading="resettingBalanceId === group.balanceId"
            @click="confirmDayUsageReset(group)"
          >
            重置日额度
          </el-button>
        </div>
      </article>
    </section>

    <section class="info-grid">
      <article class="dashboard-panel">
        <h3>说明</h3>
        <p>日卡同时受每日额度和有效期总额度限制：每日额度按自然日重置，有效期总额度用于限制整个有效期内的累计使用量。余额分组为长期余额，不受日卡规则影响。</p>
      </article>
    </section>
  </section>
</template>

<script>
import { fetchSubscriptionDashboard, resetSubscriptionDayUsage } from '../../api/subscription';

export default {
  name: 'SubscriptionView',
  data() {
    return {
      loading: false,
      resettingBalanceId: null,
      groups: []
    };
  },
  created() {
    this.loadBalances();
    this.$root.$on('checkin-completed', this.loadBalances);
  },
  beforeDestroy() {
    this.$root.$off('checkin-completed', this.loadBalances);
  },
  methods: {
    async loadBalances() {
      this.loading = true;
      try {
        const { data } = await fetchSubscriptionDashboard();
        if (!data || data.code !== 0 || !data.data) {
          throw new Error((data && data.message) || 'SUBSCRIPTION_LOAD_FAILED');
        }
        this.groups = Array.isArray(data.data.groups) ? data.data.groups : [];
      } catch (error) {
        this.groups = [];
        this.$message.error(error.message || '订阅信息加载失败');
      } finally {
        this.loading = false;
      }
    },
    formatAmount(value) {
      return `$${Number(value || 0).toFixed(4)}`;
    },
    getQuotaState(group) {
      if (!group.active) {
        return { label: group.statusText || '未开通', tone: 'inactive' };
      }

      const remainingAmount = this.getCurrentAvailableAmount(group);
      if (group.groupType === 'MONTH_CARD') {
        const totalRemainingAmount = this.getTotalRemainingAmount(group);
        if (totalRemainingAmount <= 0) {
          return { label: '有效期额度已用完', tone: 'depleted' };
        }
        if (remainingAmount <= 0) {
          return { label: '今日额度已用完，明日恢复', tone: 'daily-depleted' };
        }
        return { label: '生效中', tone: 'active' };
      }

      if (remainingAmount <= 0) {
        return { label: '余额已用完', tone: 'depleted' };
      }
      return { label: '可用', tone: 'active' };
    },
    getCurrentAvailableAmount(group) {
      const dailyOrBalanceRemaining = Math.max(0, Number(group.remainingAmount || 0));
      if (group.groupType !== 'MONTH_CARD') {
        return dailyOrBalanceRemaining;
      }
      return Math.min(dailyOrBalanceRemaining, this.getTotalRemainingAmount(group));
    },
    getTotalRemainingAmount(group) {
      const remainingAmount = group.totalRemainingAmount == null
        ? Number(group.totalAmount || 0) - Number(group.totalUsedAmount || 0)
        : Number(group.totalRemainingAmount);
      return Math.max(0, remainingAmount);
    },
    formatDateTime(value) {
      if (!value) {
        return '长期有效';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      const year = date.getFullYear();
      const month = `${date.getMonth() + 1}`.padStart(2, '0');
      const day = `${date.getDate()}`.padStart(2, '0');
      const hours = `${date.getHours()}`.padStart(2, '0');
      const minutes = `${date.getMinutes()}`.padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },
    formatResetDateTime(value) {
      if (!value) {
        return '--';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      const year = date.getFullYear();
      const month = `${date.getMonth() + 1}`.padStart(2, '0');
      const day = `${date.getDate()}`.padStart(2, '0');
      const hours = `${date.getHours()}`.padStart(2, '0');
      const minutes = `${date.getMinutes()}`.padStart(2, '0');
      const seconds = `${date.getSeconds()}`.padStart(2, '0');
      return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
    },
    canResetDayUsage(group) {
      if (!group || group.groupType !== 'MONTH_CARD' || !group.active || !group.balanceId || !group.expireTime) {
        return false;
      }
      const expireTime = new Date(group.expireTime).getTime();
      return Number.isFinite(expireTime) && expireTime > Date.now() + 24 * 60 * 60 * 1000;
    },
    async confirmDayUsageReset(group) {
      if (!this.canResetDayUsage(group) || this.resettingBalanceId !== null) {
        return;
      }

      const currentExpireTime = new Date(group.expireTime);
      const nextExpireTime = new Date(currentExpireTime.getTime() - 24 * 60 * 60 * 1000);
      const h = this.$createElement;
      try {
        await this.$confirm(
          h('div', { class: 'day-reset-confirm-content' }, [
            h('p', '重置后，当前日用量会清零，您可以继续使用今日额度。'),
            h('div', { class: 'day-reset-confirm-time' }, [
              h('span', '当前到期时间'),
              h('strong', this.formatResetDateTime(currentExpireTime))
            ]),
            h('div', { class: 'day-reset-confirm-time' }, [
              h('span', '重置后到期时间'),
              h('strong', this.formatResetDateTime(nextExpireTime))
            ]),
            h('p', { class: 'day-reset-confirm-warning' }, '本次操作会将订阅到期时间提前 24 小时。请确认后再继续。')
          ]),
          '确认重置日额度',
          {
            confirmButtonText: '确认重置',
            cancelButtonText: '取消',
            type: 'warning',
            customClass: 'day-reset-confirm-dialog'
          }
        );
      } catch (error) {
        return;
      }

      this.resettingBalanceId = group.balanceId;
      try {
        const { data } = await resetSubscriptionDayUsage(group.balanceId);
        if (!data || data.code !== 0) {
          throw new Error((data && data.message) || 'DAY_CARD_RESET_FAILED');
        }
        this.$message.success('日额度已重置');
        await this.loadBalances();
      } catch (error) {
        this.$message.error(error.message || '日额度重置失败');
      } finally {
        this.resettingBalanceId = null;
      }
    },
    getGroupLabel(groupType) {
      if (groupType === 'MONTH_CARD') {
        return '日卡分组';
      }
      if (groupType === 'BALANCE') {
        return '余额分组';
      }
      return groupType || '';
    },
    getGroupDescription(group) {
      if (group.groupType === 'MONTH_CARD') {
        return group.active
          ? '日卡同时受每日额度和有效期总额度限制，每日额度按自然日重置。'
          : '当前没有有效日卡，开通后这里会显示每日额度、有效期总额度与到期时间。';
      }
      return '余额分组用于长期余额扣费，不受日卡到期时间影响。';
    }
  }
};
</script>
